/**
 * ExpertSetupBuilder.jsx
 * 
 * A form-based product configuration component driven by backend flow configuration.
 * 
 * Key principles:
 * - NO images or visual assets - form fields only
 * - Dynamically reads step structure from flow configuration
 * - Groups steps by optional `expert_group` field, or falls back to category_id
 * - Compatible with existing flow data without requiring new metadata
 * - Does not affect Quick Order or Visual Builder
 */
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { 
    Card, 
    CardContent, 
    CardHeader, 
    CardTitle, 
    CardDescription 
} from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from './ui/select';
import { Checkbox } from './ui/checkbox';
import {
    Loader2,
    ChevronLeft,
    ChevronRight,
    Check,
    AlertCircle,
    Settings2,
    Disc,
    Palette,
    Package,
    Layers,
    Sparkles,
    Music,
    Box
} from 'lucide-react';
import { useCurrency, formatCurrencyAmount, convertFromEur } from '../hooks/useCurrency';
import { useTranslation } from '../contexts/TranslationContext';

import { API_BASE } from '../lib/apiBase';
const API_URL = API_BASE || '';

// Icon mapping for groups/categories (fallback icons)
const GROUP_ICONS = {
    format: Disc,
    speed: Disc,
    vinyl: Palette,
    vinyl_color: Palette,
    vinyl_weight: Disc,
    record_color: Palette,
    record_weight: Disc,
    label: Disc,
    center_label: Disc,
    vinyl_label: Disc,
    sleeve: Layers,
    inner_sleeve: Layers,
    cover: Package,
    cover_type: Package,
    cover_finish: Layers,
    jacket: Package,
    extras: Sparkles,
    finishing: Sparkles,
    shrink_wrap: Box,
    sticker: Sparkles,
    insert: Layers,
    test_pressings: Music,
    default: Settings2
};

/**
 * Get icon component for a group/category
 */
function getGroupIcon(groupKey) {
    const key = groupKey?.toLowerCase().replace(/[\s-]/g, '_');
    return GROUP_ICONS[key] || GROUP_ICONS.default;
}

/**
 * Format a step_id or category_id into a readable label
 */
function formatLabel(id) {
    if (!id) return '';
    return id
        .replace(/_/g, ' ')
        .replace(/-/g, ' ')
        .split(' ')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ');
}

/**
 * Group steps by their expert_group or category_id
 */
function groupSteps(steps) {
    const groups = {};
    const groupOrder = [];
    
    steps?.forEach((step, index) => {
        // Use expert_group if available, otherwise fall back to category_id
        const groupKey = step.expert_group || step.category_id || `group_${index}`;
        const groupLabel = step.expert_group_label || formatLabel(groupKey);
        
        if (!groups[groupKey]) {
            groups[groupKey] = {
                key: groupKey,
                label: groupLabel,
                icon: getGroupIcon(groupKey),
                steps: [],
                displayOrder: step.expert_display_order ?? index
            };
            groupOrder.push(groupKey);
        }
        
        groups[groupKey].steps.push(step);
    });
    
    // Sort groups by display order
    groupOrder.sort((a, b) => groups[a].displayOrder - groups[b].displayOrder);
    
    return { groups, groupOrder };
}

/**
 * AttributeField - Renders a single attribute selector field
 * Supports: text, dropdown (single select), multi-select, checkbox
 */
function AttributeField({ 
    field, 
    stepId, 
    value, 
    onChange, 
    attributeLists, 
    fetchAttributeList,
    hasError 
}) {
    const { field_id, label, field_type, source_list_id, selection_mode, required, placeholder } = field;
    
    // Fetch attribute list items if needed
    useEffect(() => {
        if (source_list_id && !attributeLists[source_list_id]) {
            fetchAttributeList(source_list_id);
        }
    }, [source_list_id, attributeLists, fetchAttributeList]);
    
    const listItems = attributeLists[source_list_id] || [];
    const isLoading = source_list_id && !attributeLists[source_list_id];
    const isMulti = selection_mode === 'multi' || field_type === 'multi-select';
    
    // Determine effective field type
    const effectiveType = field_type || (source_list_id ? 'dropdown' : 'text');
    
    return (
        <div className="space-y-1">
            <Label className="text-xs text-slate-500 flex items-center gap-1">
                {label || formatLabel(field_id)}
                {required && <span className="text-red-500">*</span>}
            </Label>
            
            {effectiveType === 'text' && !source_list_id ? (
                // Text input
                <Input
                    value={value || ''}
                    onChange={(e) => onChange(stepId, field_id, e.target.value)}
                    placeholder={placeholder || `Enter ${label || field_id}...`}
                    className={`h-8 text-sm ${hasError ? 'border-red-300 focus:border-red-500' : ''}`}
                    data-testid={`expert-attr-${field_id}`}
                />
            ) : isMulti ? (
                // Multi-select checkboxes
                <div className="space-y-1 pt-1">
                    {isLoading ? (
                        <p className="text-xs text-slate-400 flex items-center gap-1">
                            <Loader2 className="h-3 w-3 animate-spin" />
                            Loading options...
                        </p>
                    ) : listItems.length === 0 ? (
                        <p className="text-xs text-slate-400">No options available</p>
                    ) : (
                        listItems.map(item => {
                            const itemValue = item.value || item.id;
                            const isChecked = Array.isArray(value) ? value.includes(itemValue) : false;
                            return (
                                <div key={itemValue} className="flex items-center gap-2">
                                    <Checkbox
                                        id={`${stepId}-${field_id}-${itemValue}`}
                                        checked={isChecked}
                                        onCheckedChange={(checked) => {
                                            const current = Array.isArray(value) ? value : [];
                                            const newValue = checked 
                                                ? [...current, itemValue]
                                                : current.filter(v => v !== itemValue);
                                            onChange(stepId, field_id, newValue);
                                        }}
                                    />
                                    <label 
                                        htmlFor={`${stepId}-${field_id}-${itemValue}`}
                                        className="text-xs text-slate-600 cursor-pointer"
                                    >
                                        {item.label || item.name || itemValue}
                                    </label>
                                </div>
                            );
                        })
                    )}
                </div>
            ) : (
                // Dropdown single select
                <Select
                    value={value || ''}
                    onValueChange={(val) => onChange(stepId, field_id, val)}
                    disabled={isLoading}
                >
                    <SelectTrigger 
                        className={`h-8 text-sm ${hasError ? 'border-red-300' : ''}`}
                        data-testid={`expert-attr-${field_id}`}
                    >
                        <SelectValue placeholder={isLoading ? 'Loading...' : `Select ${label || ''}...`} />
                    </SelectTrigger>
                    <SelectContent>
                        {listItems.map(item => {
                            const itemValue = item.value || item.id;
                            return (
                                <SelectItem key={itemValue} value={itemValue}>
                                    {item.label || item.name || itemValue}
                                </SelectItem>
                            );
                        })}
                    </SelectContent>
                </Select>
            )}
            
            {hasError && (
                <p className="text-xs text-red-500">This field is required</p>
            )}
        </div>
    );
}

/**
 * ExpertSetupBuilder Component
 * 
 * Props:
 * - flowId: Optional explicit flow ID. If not provided, fetches by mode.
 * - flowMode: Mode to fetch flow by if flowId not provided (default: 'expert')
 * - initialSelections: Optional initial selections from saved order
 * - quantity: Initial quantity
 * - onConfigChange: Callback when config changes
 * - onComplete: Callback when configuration is confirmed
 * - onBack: Callback to go back to mode selection
 */
export default function ExpertSetupBuilder({
    flowId = null,
    flowMode = 'expert',
    initialSelections = null,
    quantity: initialQuantity = 250,
    productFamily = 'vinyl',
    onConfigChange,
    onComplete,
    onBack,
    // Landing page "Show Prices" OFF → hide the expert flow's price
    // estimate. Calc + Confirm (disabled until priceEstimate) untouched.
    showPrices = true
}) {
    // Currency handling for consistent pricing display
    const { 
        customerCurrency, 
        rates, 
        loading: currencyLoading
    } = useCurrency(localStorage.getItem('token'));
    
    // Helper function to convert EUR to customer currency
    const convertPrice = (eurAmount) => {
        if (!rates || customerCurrency === 'EUR' || !eurAmount) return eurAmount;
        return convertFromEur(eurAmount, customerCurrency, rates);
    };
    
    // Format price in customer's currency
    const formatPrice = (eurAmount, decimals = 2) => {
        if (eurAmount === null || eurAmount === undefined) return '—';
        const converted = convertPrice(eurAmount);
        return formatCurrencyAmount(converted, customerCurrency, { decimals });
    };
    
    // State
    const [flow, setFlow] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [selections, setSelections] = useState({});
    const [quantity, setQuantity] = useState(initialQuantity);
    const [priceEstimate, setPriceEstimate] = useState(null);
    const [isCalculating, setIsCalculating] = useState(false);
    const [expandedGroups, setExpandedGroups] = useState({});

    // Current display language — drives the ?lang= query param on
    // dynamic-content fetches so the configurator re-fetches labels
    // and descriptions the moment the customer changes language.
    const { language } = useTranslation();
    
    // Attribute-related state
    const [attributeValues, setAttributeValues] = useState({});  // {stepId: {fieldId: value}}
    const [attributeLists, setAttributeLists] = useState({});    // {listSlug: items[]}
    const [loadingLists, setLoadingLists] = useState({});        // {listSlug: boolean}
    
    // Visual builder assets for option labels and ordering (keyed by category_id:option_id)
    const [assetLabels, setAssetLabels] = useState({});          // {category_id:option_id: label}
    const [assetOrder, setAssetOrder] = useState({});            // {category_id:option_id: display_order}

    // Fetch attribute list items
    const fetchAttributeList = useCallback(async (listSlug) => {
        if (attributeLists[listSlug] || loadingLists[listSlug]) return;
        
        setLoadingLists(prev => ({ ...prev, [listSlug]: true }));
        try {
            const response = await fetch(`${API_URL}/api/product-config/attribute-lists/${listSlug}/items`);
            if (response.ok) {
                const data = await response.json();
                // API returns items array directly (not wrapped in {items: []})
                const items = Array.isArray(data) ? data : (data.items || []);
                setAttributeLists(prev => ({ ...prev, [listSlug]: items }));
            }
        } catch (err) {
            console.error(`[ExpertSetupBuilder] Failed to fetch attribute list ${listSlug}:`, err);
        } finally {
            setLoadingLists(prev => ({ ...prev, [listSlug]: false }));
        }
    }, [attributeLists, loadingLists]);

    // Get additional fields for currently selected option
    const getOptionAdditionalFields = useCallback((step, selectedOptionId) => {
        if (!selectedOptionId) return [];
        const option = step.options?.find(o => o.option_id === selectedOptionId);
        return option?.additional_fields || [];
    }, []);

    // Handle attribute value change
    const handleAttributeChange = useCallback((stepId, fieldId, value) => {
        setAttributeValues(prev => ({
            ...prev,
            [stepId]: {
                ...(prev[stepId] || {}),
                [fieldId]: value
            }
        }));
    }, []);

    // Validate required attributes
    const validateAttributes = useCallback(() => {
        const errors = [];
        flow?.steps?.forEach(step => {
            const selectedIds = selections[step.step_id] || [];
            const selectedOptionId = selectedIds[0];
            const additionalFields = getOptionAdditionalFields(step, selectedOptionId);
            
            additionalFields.forEach(field => {
                if (field.required) {
                    const value = attributeValues[step.step_id]?.[field.field_id];
                    if (!value || (Array.isArray(value) && value.length === 0)) {
                        errors.push({
                            stepId: step.step_id,
                            fieldId: field.field_id,
                            label: field.label,
                            message: `${field.label} is required`
                        });
                    }
                }
            });
        });
        return errors;
    }, [flow, selections, attributeValues, getOptionAdditionalFields]);

    // Fetch flow configuration
    useEffect(() => {
        const fetchFlow = async () => {
            try {
                setLoading(true);
                setError(null);
                
                // Determine endpoint: explicit flowId or by-mode
                let endpoint;
                if (flowId) {
                    endpoint = `${API_URL}/api/product-config/flows/${flowId}`;
                } else {
                    endpoint = `${API_URL}/api/product-config/flows/by-mode/${flowMode}?product_family=${encodeURIComponent(productFamily)}`;
                }
                
                const response = await fetch(endpoint);
                
                if (!response.ok) {
                    if (response.status === 404) {
                        throw new Error(`No flow configuration found${flowId ? ` for ID: ${flowId}` : ` for mode: ${flowMode}`}. Please configure a flow in the admin panel.`);
                    }
                    throw new Error('Failed to load configuration');
                }
                
                const flowData = await response.json();
                
                if (!flowData || !flowData.id) {
                    throw new Error('Invalid flow configuration received');
                }
                
                setFlow(flowData);
                
                // Initialize selections with defaults or from initialSelections
                const newSelections = {};
                flowData.steps?.forEach(step => {
                    if (initialSelections?.[step.step_id]) {
                        // Use provided initial selections
                        const initial = initialSelections[step.step_id];
                        newSelections[step.step_id] = Array.isArray(initial) ? initial : [initial];
                    } else if (step.options?.length === 1) {
                        // Auto-select if only one option available
                        newSelections[step.step_id] = [step.options[0].option_id];
                    } else {
                        // Use default option if specified
                        const defaultOption = step.options?.find(o => o.is_default);
                        if (defaultOption) {
                            newSelections[step.step_id] = [defaultOption.option_id];
                        }
                    }
                });
                setSelections(newSelections);
                
                // Initialize all groups as expanded
                const { groupOrder } = groupSteps(flowData.steps);
                setExpandedGroups(
                    groupOrder.reduce((acc, key) => ({ ...acc, [key]: true }), {})
                );
                
            } catch (err) {
                console.error('[ExpertSetupBuilder] Load error:', err);
                setError(err.message);
            } finally {
                setLoading(false);
            }
        };
        
        fetchFlow();
    }, [flowId, flowMode, productFamily, initialSelections]);

    // Fetch visual builder assets for option labels and display order
    useEffect(() => {
        if (!flow?.steps) return;
        
        const fetchAssetLabels = async () => {
            // Collect unique category IDs from all steps
            const categoryIds = new Set();
            flow.steps.forEach(step => {
                if (step.category_id) categoryIds.add(step.category_id);
            });
            
            // Fetch assets for each category and build label/order maps
            // Keys are prefixed with category_id to avoid collisions (e.g., "format:10" vs "test_pressings:10")
            const labelMap = {};
            const orderMap = {};
            
            await Promise.all([...categoryIds].map(async (categoryId) => {
                try {
                    const langQ = language && language !== 'en' ? `&lang=${encodeURIComponent(language)}` : '';
                    const response = await fetch(`${API_URL}/api/visual-builder/assets?category_id=${categoryId}${langQ}`);
                    if (response.ok) {
                        const assets = await response.json();
                        assets.forEach(asset => {
                            if (asset.option_id) {
                                const key = `${categoryId}:${asset.option_id}`;
                                if (asset.label) {
                                    labelMap[key] = asset.label;
                                }
                                if (asset.display_order !== undefined) {
                                    orderMap[key] = asset.display_order;
                                }
                            }
                        });
                    }
                } catch (err) {
                    console.error(`[ExpertSetupBuilder] Failed to fetch assets for category ${categoryId}:`, err);
                }
            }));
            
            setAssetLabels(labelMap);
            setAssetOrder(orderMap);
        };
        
        fetchAssetLabels();
    }, [flow?.steps, language]);

    // Calculate price when selections or quantity change
    useEffect(() => {
        if (!flow || Object.keys(selections).length === 0) return;
        
        const calculatePrice = async () => {
            setIsCalculating(true);
            try {
                const lineItems = [];
                
                // Build line items from selections
                flow.steps?.forEach(step => {
                    const selectedIds = selections[step.step_id] || [];
                    selectedIds.forEach(optionId => {
                        const option = step.options?.find(o => o.option_id === optionId);
                        if (option?.pricing_mapping && !option.is_included_in_base) {
                            lineItems.push({
                                sales_sku: option.pricing_mapping.sales_sku,
                                quantity_multiplier: option.pricing_mapping.quantity_multiplier || 1
                            });
                        }
                    });
                });
                
                if (lineItems.length === 0) {
                    setPriceEstimate(null);
                    return;
                }
                
                const response = await fetch(`${API_URL}/api/sales/quote`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        quantity: parseInt(quantity) || 500,
                        line_items: lineItems
                    })
                });
                
                if (response.ok) {
                    const data = await response.json();
                    setPriceEstimate({
                        total_estimate: data.total_estimate || 0,
                        unit_price_estimate: data.unit_price_estimate || 0
                    });
                }
            } catch (err) {
                console.error('[ExpertSetupBuilder] Price calculation error:', err);
            } finally {
                setIsCalculating(false);
            }
        };
        
        const timer = setTimeout(calculatePrice, 300);
        return () => clearTimeout(timer);
    }, [flow, selections, quantity]);

    // Notify parent of config changes (debounced to prevent infinite loops)
    const configRef = React.useRef(null);
    useEffect(() => {
        if (!onConfigChange || !flow) return;
        
        const config = {
            _flow_id: flow.id,
            _flow_version: flow.version,
            _flow_selections: selections,
            quantity: quantity
        };
        
        // Only notify if config actually changed
        const configString = JSON.stringify(config);
        if (configRef.current !== configString) {
            configRef.current = configString;
            onConfigChange(config, priceEstimate);
        }
    }, [flow?.id, flow?.version, selections, quantity, priceEstimate, onConfigChange]);

    // Handle selection change for a step
    const handleSelectionChange = useCallback((stepId, value, isMultiSelect = false) => {
        setSelections(prev => {
            if (isMultiSelect) {
                const current = prev[stepId] || [];
                const newSelection = current.includes(value)
                    ? current.filter(v => v !== value)
                    : [...current, value];
                return { ...prev, [stepId]: newSelection };
            }
            return { ...prev, [stepId]: [value] };
        });
    }, []);

    // Toggle group expand/collapse
    const toggleGroup = useCallback((groupKey) => {
        setExpandedGroups(prev => ({
            ...prev,
            [groupKey]: !prev[groupKey]
        }));
    }, []);

    // Get option label (from asset labels, option itself, or format option_id)
    // Uses category_id prefix to avoid collisions between steps with same option_ids
    const getOptionLabel = useCallback((option, categoryId) => {
        const key = categoryId ? `${categoryId}:${option.option_id}` : option.option_id;
        return assetLabels[key] || option.label || formatLabel(option.option_id);
    }, [assetLabels]);

    // Get sorted options based on asset display_order
    // Uses category_id prefix to get correct ordering per step
    const getSortedOptions = useCallback((options, categoryId) => {
        if (!options) return [];
        // Sort by asset display_order, keeping original order for options without display_order
        return [...options].sort((a, b) => {
            const keyA = categoryId ? `${categoryId}:${a.option_id}` : a.option_id;
            const keyB = categoryId ? `${categoryId}:${b.option_id}` : b.option_id;
            const orderA = assetOrder[keyA] ?? 9999;
            const orderB = assetOrder[keyB] ?? 9999;
            return orderA - orderB;
        });
    }, [assetOrder]);

    // Build final configuration for completion
    const buildFinalConfig = useCallback(() => {
        return {
            _flow_id: flow.id,
            _flow_version: flow.version,
            _flow_selections: selections,
            _attribute_values: attributeValues,
            quantity: quantity
        };
    }, [flow, selections, attributeValues, quantity]);

    // Handle complete button
    const [validationErrors, setValidationErrors] = useState([]);
    
    const handleComplete = useCallback(() => {
        // Validate required attributes
        const errors = validateAttributes();
        if (errors.length > 0) {
            setValidationErrors(errors);
            return;
        }
        setValidationErrors([]);
        
        if (onComplete) {
            onComplete(buildFinalConfig(), priceEstimate);
        }
    }, [onComplete, buildFinalConfig, priceEstimate, validateAttributes]);

    // Memoize grouped steps
    const { groups, groupOrder } = useMemo(() => {
        return groupSteps(flow?.steps);
    }, [flow?.steps]);

    // ==================== RENDER ====================

    // Loading state
    if (loading) {
        return (
            <Card className="w-full">
                <CardContent className="py-12 flex flex-col items-center justify-center">
                    <Loader2 className="h-8 w-8 animate-spin text-slate-400 mb-4" />
                    <p className="text-slate-500">Loading configuration...</p>
                </CardContent>
            </Card>
        );
    }

    // Error state
    if (error || !flow) {
        return (
            <Card className="w-full">
                <CardContent className="py-12 text-center">
                    <AlertCircle className="h-12 w-12 text-amber-500 mx-auto mb-4" />
                    <h3 className="text-lg font-medium text-slate-900 mb-2">Configuration Unavailable</h3>
                    <p className="text-sm text-slate-500 mb-4 max-w-md mx-auto">
                        {error || 'Unable to load configuration. Please try again or contact support.'}
                    </p>
                    {onBack && (
                        <Button variant="outline" onClick={onBack}>
                            <ChevronLeft className="h-4 w-4 mr-1" />
                            Back
                        </Button>
                    )}
                </CardContent>
            </Card>
        );
    }

    return (
        <Card className="w-full">
            <CardHeader className="pb-4">
                <div className="flex items-center justify-between">
                    <div>
                        <Badge className="mb-2 bg-slate-700 text-white">Expert Setup</Badge>
                        <CardTitle className="flex items-center gap-2">
                            <Settings2 className="h-5 w-5 text-slate-600" />
                            {flow.name || 'Product Configuration'}
                        </CardTitle>
                        <CardDescription>
                            {flow.description || 'Configure all product options'}
                        </CardDescription>
                    </div>
                    {onBack && (
                        <Button variant="ghost" size="sm" onClick={onBack}>
                            Change mode
                        </Button>
                    )}
                </div>
            </CardHeader>

            <CardContent className="space-y-4">
                {/* Quantity Input */}
                <div className="bg-slate-50 rounded-lg p-4">
                    <div className="flex items-center justify-between">
                        <div>
                            <Label className="text-sm font-medium">Quantity</Label>
                            <p className="text-xs text-slate-500">Number of units to produce</p>
                        </div>
                        <Input
                            type="number"
                            value={quantity}
                            onChange={(e) => setQuantity(Math.max(50, parseInt(e.target.value) || 50))}
                            className="w-32"
                            min={50}
                            data-testid="expert-quantity"
                        />
                    </div>
                </div>

                {/* Collapsible Groups */}
                {groupOrder.map(groupKey => {
                    const group = groups[groupKey];
                    if (!group || group.steps.length === 0) return null;
                    
                    const GroupIcon = group.icon;
                    const isExpanded = expandedGroups[groupKey] !== false;
                    
                    return (
                        <div 
                            key={groupKey}
                            className="border rounded-lg overflow-hidden"
                        >
                            {/* Group Header */}
                            <button
                                onClick={() => toggleGroup(groupKey)}
                                className="w-full flex items-center justify-between p-3 bg-slate-50 hover:bg-slate-100 transition-colors"
                                data-testid={`expert-group-${groupKey}`}
                            >
                                <div className="flex items-center gap-2">
                                    <GroupIcon className="h-4 w-4 text-slate-500" />
                                    <span className="font-medium text-slate-700">{group.label}</span>
                                    <Badge variant="secondary" className="text-xs">
                                        {group.steps.length} option{group.steps.length !== 1 ? 's' : ''}
                                    </Badge>
                                </div>
                                <ChevronRight 
                                    className={`h-4 w-4 text-slate-400 transition-transform ${isExpanded ? 'rotate-90' : ''}`} 
                                />
                            </button>
                            
                            {/* Group Content */}
                            {isExpanded && (
                                <div className="p-4 space-y-4">
                                    {group.steps.map(step => {
                                        const selectedValues = selections[step.step_id] || [];
                                        const selectedValue = selectedValues[0] || '';
                                        const isMultiSelect = step.selection_mode === 'multi' || step.expert_field_type === 'checkbox';
                                        const optionCount = step.options?.length || 0;
                                        
                                        // Sort options by admin-defined display_order (category-aware)
                                        const sortedOptions = getSortedOptions(step.options, step.category_id);
                                        
                                        // Hide step label if group has only one step (header acts as label)
                                        const hideStepLabel = group.steps.length === 1;
                                        
                                        // Smart UI selection for Expert mode:
                                        // - 1 option: auto-select, show as static text
                                        // - 2-5 options: button group for quick scanning
                                        // - 6+ options: dropdown to save space
                                        // - Multi-select: always checkboxes
                                        let renderMode = 'dropdown';
                                        if (isMultiSelect) {
                                            renderMode = 'checkbox';
                                        } else if (optionCount === 1) {
                                            renderMode = 'single';
                                        } else if (optionCount >= 2 && optionCount <= 5) {
                                            renderMode = 'buttons';
                                        }
                                        
                                        // Get additional fields for the selected option
                                        const additionalFields = getOptionAdditionalFields(step, selectedValue);
                                        
                                        // Handle option selection with attribute clearing
                                        const handleOptionSelect = (val) => {
                                            handleSelectionChange(step.step_id, val);
                                            // Clear attribute values when option changes
                                            setAttributeValues(prev => ({
                                                ...prev,
                                                [step.step_id]: {}
                                            }));
                                        };
                                        
                                        return (
                                            <div key={step.step_id} className="space-y-2">
                                                {/* Main option selector */}
                                                <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
                                                    {!hideStepLabel && (
                                                        <Label className="text-sm text-slate-600 flex items-center gap-1 min-w-[120px]">
                                                            {step.label || formatLabel(step.step_id)}
                                                            {step.is_required && (
                                                                <span className="text-red-500">*</span>
                                                            )}
                                                        </Label>
                                                    )}
                                                    
                                                    {renderMode === 'single' ? (
                                                        // Single option - show as static badge (auto-selected)
                                                        <Badge 
                                                            variant="secondary" 
                                                            className="bg-slate-100 text-slate-700 font-medium"
                                                        >
                                                            {getOptionLabel(sortedOptions[0], step.category_id)}
                                                        </Badge>
                                                    ) : renderMode === 'buttons' ? (
                                                        // Button group for 2-5 options - inline with label
                                                        <div className="flex flex-wrap gap-1.5">
                                                            {sortedOptions.map(option => {
                                                                const isSelected = selectedValue === option.option_id;
                                                                return (
                                                                    <button
                                                                        key={option.option_id}
                                                                        type="button"
                                                                        onClick={() => handleOptionSelect(option.option_id)}
                                                                        className={`px-3 py-1.5 text-sm font-medium rounded-md border transition-all ${
                                                                            isSelected
                                                                                ? 'bg-slate-900 text-white border-slate-900'
                                                                                : 'bg-white text-slate-700 border-slate-200 hover:border-slate-400 hover:bg-slate-50'
                                                                        }`}
                                                                        data-testid={`expert-btn-${step.step_id}-${option.option_id}`}
                                                                    >
                                                                        {getOptionLabel(option, step.category_id)}
                                                                    </button>
                                                                );
                                                            })}
                                                        </div>
                                                    ) : renderMode === 'checkbox' ? (
                                                        // Checkbox group for multi-select
                                                        <div className="flex flex-wrap gap-x-4 gap-y-2">
                                                            {sortedOptions.map(option => (
                                                                <div key={option.option_id} className="flex items-center gap-2">
                                                                    <Checkbox
                                                                        id={`${step.step_id}-${option.option_id}`}
                                                                        checked={selectedValues.includes(option.option_id)}
                                                                        onCheckedChange={() => handleSelectionChange(step.step_id, option.option_id, true)}
                                                                    />
                                                                    <label 
                                                                        htmlFor={`${step.step_id}-${option.option_id}`}
                                                                        className="text-sm text-slate-700 cursor-pointer"
                                                                    >
                                                                        {getOptionLabel(option, step.category_id)}
                                                                    </label>
                                                                </div>
                                                            ))}
                                                        </div>
                                                    ) : (
                                                        // Dropdown for 6+ options
                                                        <Select
                                                            value={selectedValue}
                                                            onValueChange={handleOptionSelect}
                                                        >
                                                            <SelectTrigger 
                                                                className="h-9 w-[200px]"
                                                                data-testid={`expert-select-${step.step_id}`}
                                                            >
                                                                <SelectValue placeholder="Select..." />
                                                            </SelectTrigger>
                                                            <SelectContent>
                                                                {sortedOptions.map(option => (
                                                                    <SelectItem 
                                                                        key={option.option_id} 
                                                                        value={option.option_id}
                                                                    >
                                                                        {getOptionLabel(option, step.category_id)}
                                                                    </SelectItem>
                                                                ))}
                                                            </SelectContent>
                                                        </Select>
                                                    )}
                                                </div>
                                                
                                                {/* Help text if available */}
                                                {step.help_text && (
                                                    <p className={`text-xs text-slate-400 ${!hideStepLabel ? 'ml-[136px]' : ''}`}>{step.help_text}</p>
                                                )}
                                                
                                                {/* Attribute selectors for selected option */}
                                                {additionalFields.length > 0 && (
                                                    <div className={`pl-3 border-l-2 border-slate-200 space-y-2 ${!hideStepLabel ? 'ml-[136px]' : ''}`}>
                                                        {additionalFields.map(field => (
                                                            <AttributeField
                                                                key={field.field_id}
                                                                field={field}
                                                                stepId={step.step_id}
                                                                value={attributeValues[step.step_id]?.[field.field_id]}
                                                                onChange={handleAttributeChange}
                                                                attributeLists={attributeLists}
                                                                fetchAttributeList={fetchAttributeList}
                                                                hasError={validationErrors.some(e => e.stepId === step.step_id && e.fieldId === field.field_id)}
                                                            />
                                                        ))}
                                                    </div>
                                                )}
                                            </div>
                                        );
                                    })}
                                </div>
                            )}
                        </div>
                    );
                })}

                {/* Price Estimate & Confirm */}
                <div className="border-t pt-4 mt-6">
                    <div className="flex items-center justify-between">
                        <div>
                            {showPrices && <p className="text-sm text-slate-500">Estimated price</p>}
                            {showPrices && (priceEstimate && !currencyLoading ? (
                                <>
                                    <p className="text-2xl font-bold text-emerald-600 font-display">
                                        {formatPrice(priceEstimate.unit_price_estimate, 2)}
                                        <span className="text-sm font-medium text-emerald-700 ml-1">/ unit</span>
                                    </p>
                                    <p className="text-xs text-slate-400 mt-0.5">
                                        {formatPrice(priceEstimate.total_estimate, 2)} total
                                    </p>
                                </>
                            ) : isCalculating || currencyLoading ? (
                                <div className="flex items-center gap-2">
                                    <Loader2 className="h-4 w-4 animate-spin" />
                                    <span className="text-sm text-slate-500">Calculating...</span>
                                </div>
                            ) : (
                                <p className="text-sm text-slate-400">Select options to get estimate</p>
                            ))}
                        </div>
                        <Button 
                            onClick={handleComplete}
                            disabled={!priceEstimate}
                            data-testid="expert-confirm"
                        >
                            <Check className="h-4 w-4 mr-2" />
                            Confirm Configuration
                        </Button>
                    </div>
                </div>
            </CardContent>
        </Card>
    );
}
